import EvaluateRequest from "../EvaluateRequest"
import EvaluateResponse from "../EvaluateResponse"
import Evaluation from "../Evaluation"
import Entity from "../../model/Entity"

export default interface Evaluator<REQUEST extends EvaluateRequest, RESPONSE extends EvaluateResponse> {
  evaluate(request: REQUEST, context: EvaluatorContext): RESPONSE

  record(request: REQUEST, response: RESPONSE): void
}

type Constructor<T> = abstract new (...args: any[]) => T

export class EvaluatorContext {
  private readonly _stack: EvaluateRequest[]
  private readonly _references: Evaluation[]
  private readonly map: WeakMap<Constructor<any>, any>

  private constructor() {
    this._stack = []
    this._references = []
    this.map = new WeakMap()
  }

  static create(): EvaluatorContext {
    return new EvaluatorContext()
  }

  get stack(): EvaluateRequest[] {
    return Array.from(this._stack)
  }

  get references(): Evaluation[] {
    return Array.from(this._references)
  }

  contains(request: EvaluateRequest): boolean {
    return this._stack.some(
      (it) => it.entity.serviceType === request.entity.serviceType && it.entity.id === request.entity.id
    )
  }

  addRequest(request: EvaluateRequest): void {
    this._stack.push(request)
  }

  removeRequest(request: EvaluateRequest): void {
    const index = this._stack.indexOf(request, 0)
    if (index > -1) {
      this._stack.splice(index, 1)
    }
  }

  getEvaluation(entity: Entity): Evaluation | undefined {
    return this._references.find((it) => it.entity.serviceType === entity.serviceType && it.entity.id === entity.id)
  }

  addEvaluation(evaluation: Evaluation): void {
    this._references.push(evaluation)
  }

  get<R, T extends Constructor<R> = Constructor<R>>(key: T): InstanceType<T> | null {
    const value = this.map.get(key)
    if (!value) return null

    // NoSuchElementException
    if (!(value instanceof key)) {
      throw new Error(`Context does not contain a value of type ${key.name}`)
    }
    return value as InstanceType<T>
  }

  set<R, T extends Constructor<R> = Constructor<R>>(key: T, value: InstanceType<typeof key>): void {
    this.map.set(key, value)
  }
}
